import { Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@istari-frontend/ui";
import { useId } from "react";

import type { InfosecLevelOption } from "~/ai-chat/ui/chat-save-resource-card";
import { cn } from "~/utils/cn";
import { formatInfosecLevelName } from "~/utils/infosec";

interface ChatClassifyFieldsProps {
  name: string;
  onNameChange: (name: string) => void;
  // Whether to show the (required) infosec level select — off on deployments without infosec.
  showInfosecLevel: boolean;
  infosecLevels: InfosecLevelOption[];
  infosecLevelId: string | null;
  onInfosecLevelChange: (id: string | null) => void;
}

// The name + infosec-classification inputs shared by the save dialog and the share modal's classify step.
export function ChatClassifyFields({
  name,
  onNameChange,
  showInfosecLevel,
  infosecLevels,
  infosecLevelId,
  onInfosecLevelChange,
}: ChatClassifyFieldsProps) {
  const nameInputId = useId();
  const infosecLevelSelectId = useId();
  // A required level is missing only when the select is shown and still empty — derived here so a caller
  // can't pass a value that disagrees with the rendered red-border state.
  const isMissingInfosecLevel = showInfosecLevel && infosecLevelId === null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={nameInputId} className="text-xs">
          Name
        </Label>
        <Input
          id={nameInputId}
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
          placeholder="Istari chat"
        />
      </div>
      {showInfosecLevel && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={infosecLevelSelectId} className="text-xs">
            Infosec level <span className="text-destructive">*</span>
          </Label>
          <Select value={infosecLevelId ?? ""} onValueChange={(value) => onInfosecLevelChange(value || null)}>
            <SelectTrigger
              id={infosecLevelSelectId}
              className={cn(isMissingInfosecLevel && "border-destructive ring-destructive/20")}
              aria-invalid={isMissingInfosecLevel}
            >
              <SelectValue placeholder="Select level" />
            </SelectTrigger>
            <SelectContent>
              {infosecLevels.map((level) => (
                <SelectItem key={level.id} value={level.id}>
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full" style={{ backgroundColor: level.displayColor }} />
                    {formatInfosecLevelName(level.name)}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
